import React from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { fetchForm, updateData } from "../actions";
import {
  buildPersistedGeneralDetails,
  normalizeGeneralDetails,
} from "./form-v2/generalDetailsUtils";
import {
  buildPersistedAddressDetails,
  normalizeAddressDetails,
  validateAddressHistory,
} from "./form-v2/addressUtils";
import {
  buildPersistedEmploymentDetails,
  normalizeEmploymentDetails,
  validateEmploymentHistory,
} from "./form-v2/employmentUtils";
import {
  buildPersistedIncomeDetails,
  normalizeIncomeDetails,
  calculateTotal,
} from "./form-v2/incomeUtils";
import {
  buildPersistedLiabilitiesDetails,
  normalizeLiabilitiesDetails,
  createLoanId,
  createCreditCardId,
  buildEmptyLoan,
  buildEmptyCreditCard,
} from "./form-v2/liabilitiesUtils";
import {
  buildPersistedAssetDetails,
  normalizeAssetDetails,
  createAssetId,
  buildEmptyProperty,
  buildEmptySaving,
  buildEmptyShare,
  buildEmptySuper,
  buildEmptyVehicle,
  buildEmptyAsset,
} from "./form-v2/assetsUtils";
import {
  buildPersistedLoanRequirements,
  normalizeLoanRequirements,
} from "./form-v2/loanRequirementsUtils";
import { copyApplicantData } from "./form-v2/copyApplicantUtils";

/**
 * FormV2 - Mobile-first mortgage fact find form (Category-by-category implementation)
 * 
 * Applicant selection model:
 * - Users select their applicant at the start (Applicant_Select step)
 * - They can only switch applicants by returning to the selection screen
 * - No applicant switcher is shown during form completion
 * 
 * Copy feature (for non-personal sections):
 * - General Details: NO copy (personal information)
 * - Address: YES - copy current/previous addresses from another applicant
 * - Employment: NO copy (individual employment history)
 * - Income: YES - copy shared expenses
 * - Liabilities: YES - copy joint debts
 * - Assets: YES - copy jointly owned assets
 * - Loan Requirements: YES - copy shared goals
 * 
 * Copy utilities are in: ./form-v2/copyApplicantUtils.js
 */

class FormV2 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: "Applicant_Select",
      timeoutID: null,
      hasHydratedFromStore: false,
      categorySaved: false,
      validationError: "",
      generalDetails: normalizeGeneralDetails({}),
      addressDetails: normalizeAddressDetails({}),
      employmentDetails: normalizeEmploymentDetails({}),
      incomeDetails: normalizeIncomeDetails({}),
      liabilitiesDetails: normalizeLiabilitiesDetails({}),
      assetDetails: normalizeAssetDetails({}),
      loanRequirements: normalizeLoanRequirements({}),
      selectedApplicantId: "applicant1",
    };
  }

  componentDidMount() {
    if (this.isPreviewMode()) {
      this.loadTestData();
      return;
    }
    this.props.fetchForm(this.props.match.params.id);
  }

  loadTestData = () => {
    const testGeneralDetails = {
      applicant_order: ["applicant1", "applicant2"],
      applicants: {
        applicant1: {
          title: "Mr",
          given_name: "John",
          middle_name: "William",
          family_name: "Smith",
          home_phone: "02 9876 5432",
          mobile: "0412 345 678",
          work_phone: "02 8765 4321",
          email: "john.smith@email.com",
          dob: { day: "15", month: "06", year: "1985" },
          licence_expiry: { day: "15", month: "06", year: "2028" },
          gender: "Male",
          martial_status: "Married",
          maiden_name: "",
          resident_status: "Citizen",
          dependant_children: "2",
          dependant_child_age_1: "8",
          dependant_child_age_2: "5",
        },
        applicant2: {
          title: "Mrs",
          given_name: "Sarah",
          middle_name: "Jane",
          family_name: "Smith",
          home_phone: "02 9876 5432",
          mobile: "0423 456 789",
          work_phone: "02 8765 9999",
          email: "sarah.smith@email.com",
          dob: { day: "22", month: "09", year: "1987" },
          licence_expiry: { day: "22", month: "09", year: "2027" },
          gender: "Female",
          martial_status: "Married",
          maiden_name: "Johnson",
          resident_status: "Citizen",
          dependant_children: "2",
          dependant_child_age_1: "8",
          dependant_child_age_2: "5",
        },
      },
    };

    const testAddressDetails = {
      applicant_order: ["applicant1", "applicant2"],
      applicants: {
        applicant1: {
          current: {
            street: "123 George Street",
            suburb: "Sydney",
            state: "NSW",
            postcode: "2000",
            address_type: "Owned",
            address_amount: "",
            other_text: "",
            moved_in: { day: "10", month: "03", year: "2020" },
          },
          previous: [
            {
              variable: 1,
              street: "45 Pitt Street",
              suburb: "Sydney",
              state: "NSW",
              postcode: "2000",
              address_type: "Renting",
              address_amount: "650",
              other_text: "",
              moved_in: { day: "15", month: "01", year: "2018" },
              moved_out: { day: "09", month: "03", year: "2020" },
            },
          ],
        },
        applicant2: {
          current: {
            street: "123 George Street",
            suburb: "Sydney",
            state: "NSW",
            postcode: "2000",
            address_type: "Owned",
            address_amount: "",
            other_text: "",
            moved_in: { day: "10", month: "03", year: "2020" },
          },
          previous: [
            {
              variable: 1,
              street: "78 Market Street",
              suburb: "Melbourne",
              state: "VIC",
              postcode: "3000",
              address_type: "Living with parents",
              address_amount: "",
              other_text: "",
              moved_in: { day: "01", month: "05", year: "2017" },
              moved_out: { day: "09", month: "03", year: "2020" },
            },
          ],
        },
      },
    };

    const testEmploymentDetails = {
      applicant_order: ["applicant1", "applicant2"],
      other_income_details: "",
      applicants: {
        applicant1: {
          current: {
            position: "Senior Software Engineer",
            employer_name: "Tech Solutions Pty Ltd",
            employer_phone: "02 9123 4567",
            gross_income: "120000",
            employment_status: "Full Time",
            start_date: { day: "15", month: "01", year: "2019" },
          },
          previous: [
            {
              variable: 1,
              position: "Software Developer",
              employer_name: "Digital Agency Co",
              employer_phone: "02 8765 4321",
              gross_income: "85000",
              employment_status: "Full Time",
              start_date: { day: "10", month: "06", year: "2016" },
              end_date: { day: "14", month: "01", year: "2019" },
            },
          ],
        },
        applicant2: {
          current: {
            position: "Marketing Manager",
            employer_name: "Brand Marketing Group",
            employer_phone: "02 9555 7777",
            gross_income: "95000",
            employment_status: "Full Time",
            start_date: { day: "01", month: "03", year: "2020" },
          },
          previous: [
            {
              variable: 1,
              position: "Marketing Coordinator",
              employer_name: "Media House Pty Ltd",
              employer_phone: "03 9123 8888",
              gross_income: "65000",
              employment_status: "Full Time",
              start_date: { day: "05", month: "02", year: "2017" },
              end_date: { day: "28", month: "02", year: "2020" },
            },
          ],
        },
      },
    };

    const testIncomeDetails = {
      monthly_living_expenses_post_loan: {
        groceries: "800",
        clothing: "300",
        transport: "400",
        communication: "150",
        recreation: "500",
        child_care: "600",
        pet_care: "100",
        health: "200",
        health_insurance: "350",
        general_insurance: "150",
        sickness: "100",
        public_school: "50",
        private_school: "",
        higher_edu: "",
        other: "Additional household costs",
      },
    };

    const testLiabilitiesDetails = {
      loans: {
        "1": {
          bank_name: "Commonwealth Bank",
          owing: "450000",
          repayment: "2500",
          limit: "500000",
          held_by: "Jointly",
          payout: "No",
          applicant1: true,
          applicant2: true,
        },
      },
      credit_cards: {
        "1": {
          bank_name: "Westpac",
          owing: "5000",
          limit: "15000",
          held_by: "Applicant 1",
          payout: "No",
          applicant1: true,
          applicant2: false,
        },
      },
    };

    const testAssetDetails = {
      properties: {
        "1": {
          address: "123 George Street, Sydney NSW 2000",
          value: "850000",
          owing: "450000",
          rental_income: "",
          payout: "No",
          held_by: "Jointly",
          applicant1: true,
          applicant2: true,
        },
      },
      home_contents_value: "50000",
      savings: {
        "1": {
          name: "CBA Savings Account",
          value: "25000",
          held_by: "Jointly",
          applicant1: true,
          applicant2: true,
        },
      },
      shares: {},
      supers: {
        "1": {
          name: "Australian Super",
          value: "180000",
          held_by: "Applicant 1",
          applicant1: true,
          applicant2: false,
        },
        "2": {
          name: "REST Super",
          value: "120000",
          held_by: "Applicant 2",
          applicant1: false,
          applicant2: true,
        },
      },
      vehicles: {
        "1": {
          make: "Toyota",
          model: "Camry",
          year: "2020",
          value: "28000",
          owing: "",
          payout: "",
          held_by: "Jointly",
          applicant1: true,
          applicant2: true,
        },
      },
      assets: {},
    };

    const testLoanRequirements = {
      why: {
        wide_choice: true,
        expert_advice: true,
        convienence: false,
        legwork: false,
        existing_customer: false,
      },
      what_can_assist: {
        purchase: false,
        refinance: true,
        build_new_home: false,
        existing_loan: false,
      },
      property: "Refinance existing property at 123 George Street",
      payment_plan: {
        repayment_prior_to_retirement: true,
        downsizing: false,
        sale_of_assets: false,
        super_income: false,
        super_lump_sum: false,
        savings: true,
        other_investments: false,
        co_app_income: true,
        other_check: false,
        other: "",
      },
      loan_term: "30",
      loan_term_other: "",
      changes_in_financial_circum: "No",
      changes_in_financial_circum_yes: "",
      loan_repayment_type: "Principal and Interest",
      interest_only_term: "",
      rate_type: "Variable",
      important_features: {
        basic_home_loan: { required: true, importance: "Essential" },
        offset_account: { required: true, importance: "Important" },
        redraw_facility: { required: true, importance: "Nice to have" },
        professional_packages: { required: false, importance: "" },
        extra_repayments_allowed: { required: true, importance: "Essential" },
      },
    };

    this.setState({
      generalDetails: testGeneralDetails,
      addressDetails: testAddressDetails,
      employmentDetails: testEmploymentDetails,
      incomeDetails: testIncomeDetails,
      liabilitiesDetails: testLiabilitiesDetails,
      assetDetails: testAssetDetails,
      loanRequirements: testLoanRequirements,
      selectedApplicantId: "applicant1",
      hasHydratedFromStore: true,
    });
  };

  componentDidUpdate(prevProps) {
    if (this.isPreviewMode()) {
      return;
    }

    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.props.fetchForm(this.props.match.params.id);
      this.setState({
        hasHydratedFromStore: false,
        categorySaved: false,
      });
    }

    if (
      !this.state.hasHydratedFromStore &&
      this.props.data &&
      this.props.applicants &&
      Object.keys(this.props.applicants).length > 0 &&
      this.props.data !== prevProps.data
    ) {
      const applicantIds = Object.keys(this.props.applicants);
      
      const generalDetails = this.props.data.General_Details
        ? normalizeGeneralDetails(this.props.data.General_Details)
        : normalizeGeneralDetails({});

      if (generalDetails.applicant_order.length === 0 || generalDetails.applicant_order[0] === "applicant1") {
        generalDetails.applicant_order = applicantIds;
        applicantIds.forEach((id) => {
          if (!generalDetails.applicants[id]) {
            generalDetails.applicants[id] = normalizeGeneralDetails({}).applicants.applicant1;
          }
        });
      }

      const addressDetails = this.props.data.Address_Details
        ? normalizeAddressDetails(this.props.data.Address_Details)
        : normalizeAddressDetails({});

      if (addressDetails.applicant_order.length === 0 || addressDetails.applicant_order[0] === "applicant1") {
        addressDetails.applicant_order = applicantIds;
        applicantIds.forEach((id) => {
          if (!addressDetails.applicants[id]) {
            addressDetails.applicants[id] = {
              current: normalizeAddressDetails({}).applicants.applicant1.current,
              previous: [],
            };
          }
        });
      }

      const employmentDetails = this.props.data.Employment_Details
        ? normalizeEmploymentDetails(this.props.data.Employment_Details)
        : normalizeEmploymentDetails({});

      if (employmentDetails.applicant_order.length === 0 || employmentDetails.applicant_order[0] === "applicant1") {
        employmentDetails.applicant_order = applicantIds;
        applicantIds.forEach((id) => {
          if (!employmentDetails.applicants[id]) {
            employmentDetails.applicants[id] = {
              current: normalizeEmploymentDetails({}).applicants.applicant1.current,
              previous: [],
            };
          }
        });
      }

      const incomeDetails = this.props.data.Income_Details
        ? normalizeIncomeDetails(this.props.data.Income_Details)
        : normalizeIncomeDetails({});

      const liabilitiesDetails = this.props.data.Liabilities_Debts_Details
        ? normalizeLiabilitiesDetails(this.props.data.Liabilities_Debts_Details)
        : normalizeLiabilitiesDetails({});

      const assetDetails = this.props.data.Asset_Details
        ? normalizeAssetDetails(this.props.data.Asset_Details)
        : normalizeAssetDetails({});

      const loanRequirements = this.props.data.Loan_Requirements
        ? normalizeLoanRequirements(this.props.data.Loan_Requirements)
        : normalizeLoanRequirements({});

      this.setState({
        generalDetails,
        addressDetails,
        employmentDetails,
        incomeDetails,
        liabilitiesDetails,
        assetDetails,
        loanRequirements,
        selectedApplicantId: generalDetails.applicant_order[0],
        hasHydratedFromStore: true,
      });
    }
  }

  componentWillUnmount() {
    if (this.state.timeoutID) {
      clearTimeout(this.state.timeoutID);
    }
  }

  queueSave = () => {
    if (this.isPreviewMode()) {
      return;
    }

    if (this.state.timeoutID) {
      clearTimeout(this.state.timeoutID);
    }

    const timeoutID = setTimeout(() => {
      const step = this.state.currentStep;
      if (step === "General_Details") {
        this.saveGeneralDetails();
      } else if (step === "Address_3y") {
        this.saveAddressDetails();
      } else if (step === "Employment_Details") {
        this.saveEmploymentDetails();
      } else if (step === "Income_Details") {
        this.saveIncomeDetails();
      } else if (step === "Liabilities_Debts") {
        this.saveLiabilitiesDetails();
      } else if (step === "Assets") {
        this.saveAssetDetails();
      } else if (step === "Loan_Requirements") {
        this.saveLoanRequirements();
      }
    }, 300);

    this.setState({ timeoutID, categorySaved: false });
  };

  saveGeneralDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedGeneralDetails(this.state.generalDetails);
    this.props.updateData({
      state: persistedState,
      type: "General_Details",
    });
  };

  setCurrentStep = (step) => {
    window.scrollTo(0, 0);
    this.setState({ currentStep: step });
  };

  setSelectedApplicant = (id) => {
    this.setState({ selectedApplicantId: id });
  };

  updateApplicantField = (field, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentApplicant = this.state.generalDetails.applicants[selectedApplicantId] || {};
    const nextApplicant = { ...currentApplicant, [field]: value };
    const nextGeneralDetails = {
      ...this.state.generalDetails,
      applicants: {
        ...this.state.generalDetails.applicants,
        [selectedApplicantId]: nextApplicant,
      },
    };

    this.setState({ generalDetails: nextGeneralDetails });
    this.queueSave();
  };

  updateApplicantDatePart = (field, part, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentApplicant = this.state.generalDetails.applicants[selectedApplicantId] || {};
    const currentField = currentApplicant[field] || {};
    const nextApplicant = {
      ...currentApplicant,
      [field]: {
        ...currentField,
        [part]: value,
      },
    };
    const nextGeneralDetails = {
      ...this.state.generalDetails,
      applicants: {
        ...this.state.generalDetails.applicants,
        [selectedApplicantId]: nextApplicant,
      },
    };

    this.setState({ generalDetails: nextGeneralDetails });
    this.queueSave();
  };

  updateDependantChildAge = (index, value) => {
    const key = `dependant_child_age_${index + 1}`;
    this.updateApplicantField(key, value);
  };

  completeGeneralDetailsCategory = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    this.saveGeneralDetails();
    this.setState({ currentStep: "Address_3y", categorySaved: false, validationError: "" });
  };

  // ============ ADDRESS METHODS ============

  saveAddressDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedAddressDetails(this.state.addressDetails);
    this.props.updateData({
      state: persistedState,
      type: "Address_Details",
    });
  };

  updateAddressField = (field, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentAddress = this.state.addressDetails.applicants[selectedApplicantId]?.current || {};
    const nextAddress = { ...currentAddress, [field]: value };
    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...this.state.addressDetails.applicants[selectedApplicantId],
          current: nextAddress,
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  updateAddressDatePart = (field, part, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentAddress = this.state.addressDetails.applicants[selectedApplicantId]?.current || {};
    const currentField = currentAddress[field] || {};
    const nextAddress = {
      ...currentAddress,
      [field]: {
        ...currentField,
        [part]: value,
      },
    };
    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...this.state.addressDetails.applicants[selectedApplicantId],
          current: nextAddress,
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  addPreviousAddress = () => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId];
    const newIndex = applicantAddress.previous.length + 1;
    const newPreviousAddress = {
      variable: newIndex,
      street: "",
      suburb: "",
      state: "",
      postcode: "",
      address_type: "",
      address_amount: "",
      other_text: "",
      moved_in: { day: "", month: "", year: "" },
      moved_out: { day: "", month: "", year: "" },
    };

    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...applicantAddress,
          previous: [...applicantAddress.previous, newPreviousAddress],
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  deletePreviousAddress = (index) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId];
    const updatedPrevious = applicantAddress.previous.filter((_, i) => i !== index);

    updatedPrevious.forEach((addr, i) => {
      addr.variable = i + 1;
    });

    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...applicantAddress,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  updatePreviousAddressField = (index, field, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId];
    const updatedPrevious = [...applicantAddress.previous];
    updatedPrevious[index] = {
      ...updatedPrevious[index],
      [field]: value,
    };

    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...applicantAddress,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  updatePreviousAddressDatePart = (index, field, part, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId];
    const updatedPrevious = [...applicantAddress.previous];
    updatedPrevious[index] = {
      ...updatedPrevious[index],
      [field]: {
        ...updatedPrevious[index][field],
        [part]: value,
      },
    };

    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: {
          ...applicantAddress,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  copyAddressFromApplicant = (sourceApplicantId) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const sourceAddress = this.state.addressDetails.applicants[sourceApplicantId];
    
    if (!sourceAddress) return;

    const copiedAddress = {
      current: { ...sourceAddress.current },
      previous: sourceAddress.previous.map((addr) => ({ ...addr })),
    };

    const nextAddressDetails = {
      ...this.state.addressDetails,
      applicants: {
        ...this.state.addressDetails.applicants,
        [selectedApplicantId]: copiedAddress,
      },
    };

    this.setState({ addressDetails: nextAddressDetails });
    this.queueSave();
  };

  completeAddressCategory = (e) => {
    e.preventDefault();
    
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId];
    
    const isValid = validateAddressHistory(applicantAddress, 3);
    
    if (!isValid) {
      window.scrollTo(0, 0);
      this.setState({
        validationError: "Please provide at least 3 years of address history (current + previous addresses).",
      });
      return;
    }

    window.scrollTo(0, 0);
    this.saveAddressDetails();
    this.setState({ currentStep: "Employment_Details", categorySaved: false, validationError: "" });
  };

  // ============ EMPLOYMENT METHODS ============

  saveEmploymentDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedEmploymentDetails(this.state.employmentDetails);
    this.props.updateData({
      state: persistedState,
      type: "Employment_Details",
    });
  };

  updateEmploymentField = (field, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentEmployment = this.state.employmentDetails.applicants[selectedApplicantId]?.current || {};
    const nextEmployment = { ...currentEmployment, [field]: value };
    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...this.state.employmentDetails.applicants[selectedApplicantId],
          current: nextEmployment,
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  updateEmploymentDatePart = (field, part, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const currentEmployment = this.state.employmentDetails.applicants[selectedApplicantId]?.current || {};
    const currentField = currentEmployment[field] || {};
    const nextEmployment = {
      ...currentEmployment,
      [field]: {
        ...currentField,
        [part]: value,
      },
    };
    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...this.state.employmentDetails.applicants[selectedApplicantId],
          current: nextEmployment,
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  addPreviousEmployment = () => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId];
    const newIndex = applicantEmployment.previous.length + 1;
    const newPreviousEmployment = {
      variable: newIndex,
      position: "",
      employer_name: "",
      employer_phone: "",
      gross_income: "",
      employment_status: "",
      start_date: { day: "", month: "", year: "" },
      end_date: { day: "", month: "", year: "" },
    };

    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...applicantEmployment,
          previous: [...applicantEmployment.previous, newPreviousEmployment],
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  deletePreviousEmployment = (index) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId];
    const updatedPrevious = applicantEmployment.previous.filter((_, i) => i !== index);

    updatedPrevious.forEach((emp, i) => {
      emp.variable = i + 1;
    });

    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...applicantEmployment,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  updatePreviousEmploymentField = (index, field, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId];
    const updatedPrevious = [...applicantEmployment.previous];
    updatedPrevious[index] = {
      ...updatedPrevious[index],
      [field]: value,
    };

    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...applicantEmployment,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  updatePreviousEmploymentDatePart = (index, field, part, value) => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId];
    const updatedPrevious = [...applicantEmployment.previous];
    updatedPrevious[index] = {
      ...updatedPrevious[index],
      [field]: {
        ...updatedPrevious[index][field],
        [part]: value,
      },
    };

    const nextEmploymentDetails = {
      ...this.state.employmentDetails,
      applicants: {
        ...this.state.employmentDetails.applicants,
        [selectedApplicantId]: {
          ...applicantEmployment,
          previous: updatedPrevious,
        },
      },
    };

    this.setState({ employmentDetails: nextEmploymentDetails });
    this.queueSave();
  };

  completeEmploymentCategory = (e) => {
    e.preventDefault();
    
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId];
    
    const isValid = validateEmploymentHistory(applicantEmployment, 3);
    
    if (!isValid) {
      window.scrollTo(0, 0);
      this.setState({
        validationError: "Please provide at least 3 years of employment history (current + previous employment).",
      });
      return;
    }

    window.scrollTo(0, 0);
    this.saveEmploymentDetails();
    this.setState({ currentStep: "Income_Details", categorySaved: false, validationError: "" });
  };

  // ============ INCOME METHODS ============

  saveIncomeDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedIncomeDetails(this.state.incomeDetails);
    this.props.updateData({
      state: persistedState,
      type: "Income_Details",
    });
  };

  updateIncomeField = (field, value) => {
    const nextIncomeDetails = {
      ...this.state.incomeDetails,
      monthly_living_expenses_post_loan: {
        ...this.state.incomeDetails.monthly_living_expenses_post_loan,
        [field]: value,
      },
    };
    this.setState({ incomeDetails: nextIncomeDetails });
    this.queueSave();
  };

  completeIncomeCategory = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    this.saveIncomeDetails();
    this.setState({ currentStep: "Liabilities_Debts", categorySaved: false, validationError: "" });
  };

  // ============ LIABILITIES METHODS ============

  saveLiabilitiesDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedLiabilitiesDetails(this.state.liabilitiesDetails);
    this.props.updateData({
      state: persistedState,
      type: "Liabilities_Debts_Details",
    });
  };

  addLoan = () => {
    const newLoanId = createLoanId(this.state.liabilitiesDetails.loans);
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      loans: {
        ...this.state.liabilitiesDetails.loans,
        [newLoanId]: buildEmptyLoan(),
      },
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
  };

  deleteLoan = (loanId) => {
    const nextLoans = { ...this.state.liabilitiesDetails.loans };
    delete nextLoans[loanId];
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      loans: nextLoans,
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
    this.queueSave();
  };

  updateLoanField = (loanId, field, value) => {
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      loans: {
        ...this.state.liabilitiesDetails.loans,
        [loanId]: {
          ...this.state.liabilitiesDetails.loans[loanId],
          [field]: value,
        },
      },
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
    this.queueSave();
  };

  addCreditCard = () => {
    const newCardId = createCreditCardId(this.state.liabilitiesDetails.credit_cards);
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      credit_cards: {
        ...this.state.liabilitiesDetails.credit_cards,
        [newCardId]: buildEmptyCreditCard(),
      },
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
  };

  deleteCreditCard = (cardId) => {
    const nextCards = { ...this.state.liabilitiesDetails.credit_cards };
    delete nextCards[cardId];
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      credit_cards: nextCards,
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
    this.queueSave();
  };

  updateCreditCardField = (cardId, field, value) => {
    const nextLiabilitiesDetails = {
      ...this.state.liabilitiesDetails,
      credit_cards: {
        ...this.state.liabilitiesDetails.credit_cards,
        [cardId]: {
          ...this.state.liabilitiesDetails.credit_cards[cardId],
          [field]: value,
        },
      },
    };
    this.setState({ liabilitiesDetails: nextLiabilitiesDetails });
    this.queueSave();
  };

  completeLiabilitiesCategory = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    this.saveLiabilitiesDetails();
    this.setState({ currentStep: "Assets", categorySaved: false, validationError: "" });
  };

  // ============ ASSETS METHODS ============

  saveAssetDetails = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedAssetDetails(this.state.assetDetails);
    this.props.updateData({
      state: persistedState,
      type: "Asset_Details",
    });
  };

  updateHomeContentsValue = (value) => {
    const nextAssetDetails = {
      ...this.state.assetDetails,
      home_contents_value: value,
    };
    this.setState({ assetDetails: nextAssetDetails });
    this.queueSave();
  };

  addAsset = (type) => {
    const existingAssets = this.state.assetDetails[type];
    const newAssetId = createAssetId(existingAssets, type);
    let emptyAsset;

    switch (type) {
      case "properties":
        emptyAsset = buildEmptyProperty();
        break;
      case "savings":
        emptyAsset = buildEmptySaving();
        break;
      case "shares":
        emptyAsset = buildEmptyShare();
        break;
      case "supers":
        emptyAsset = buildEmptySuper();
        break;
      case "vehicles":
        emptyAsset = buildEmptyVehicle();
        break;
      case "assets":
        emptyAsset = buildEmptyAsset();
        break;
      default:
        emptyAsset = {};
    }

    const nextAssetDetails = {
      ...this.state.assetDetails,
      [type]: {
        ...this.state.assetDetails[type],
        [newAssetId]: emptyAsset,
      },
    };
    this.setState({ assetDetails: nextAssetDetails });
  };

  deleteAsset = (type, assetId) => {
    const nextAssets = { ...this.state.assetDetails[type] };
    delete nextAssets[assetId];
    const nextAssetDetails = {
      ...this.state.assetDetails,
      [type]: nextAssets,
    };
    this.setState({ assetDetails: nextAssetDetails });
    this.queueSave();
  };

  updateAssetField = (type, assetId, field, value) => {
    const nextAssetDetails = {
      ...this.state.assetDetails,
      [type]: {
        ...this.state.assetDetails[type],
        [assetId]: {
          ...this.state.assetDetails[type][assetId],
          [field]: value,
        },
      },
    };
    this.setState({ assetDetails: nextAssetDetails });
    this.queueSave();
  };

  completeAssetsCategory = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    this.saveAssetDetails();
    this.setState({ currentStep: "Loan_Requirements", categorySaved: false, validationError: "" });
  };

  // ============ LOAN REQUIREMENTS METHODS ============

  saveLoanRequirements = () => {
    if (this.isPreviewMode()) {
      return;
    }
    const persistedState = buildPersistedLoanRequirements(this.state.loanRequirements);
    this.props.updateData({
      state: persistedState,
      type: "Loan_Requirements",
    });
  };

  updateLoanRequirementField = (field, value) => {
    const nextLoanRequirements = {
      ...this.state.loanRequirements,
      [field]: value,
    };
    this.setState({ loanRequirements: nextLoanRequirements });
    this.queueSave();
  };

  toggleLoanRequirementCheckbox = (category, field) => {
    const nextLoanRequirements = {
      ...this.state.loanRequirements,
      [category]: {
        ...this.state.loanRequirements[category],
        [field]: !this.state.loanRequirements[category][field],
      },
    };
    this.setState({ loanRequirements: nextLoanRequirements });
    this.queueSave();
  };

  updateImportantFeature = (feature, subfield, value) => {
    const nextLoanRequirements = {
      ...this.state.loanRequirements,
      important_features: {
        ...this.state.loanRequirements.important_features,
        [feature]: {
          ...this.state.loanRequirements.important_features[feature],
          [subfield]: value,
        },
      },
    };
    this.setState({ loanRequirements: nextLoanRequirements });
    this.queueSave();
  };

  completeLoanRequirementsCategory = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    this.saveLoanRequirements();
    this.setState({ currentStep: "Complete", categorySaved: true, validationError: "" });
  };

  isPreviewMode = () => {
    return this.props.match?.params?.id === "test";
  };

  getProgressPercentage = () => {
    const step = this.state.currentStep;
    const progressMap = {
      Applicant_Select: 0,
      General_Details: 15,
      Address_3y: 30,
      Employment_Details: 45,
      Income_Details: 60,
      Liabilities_Debts: 75,
      Assets: 90,
      Loan_Requirements: 98,
      Complete: 100,
    };
    return progressMap[step] || 0;
  };

  renderProgressBar = () => {
    const percentage = this.getProgressPercentage();
    if (percentage === 0) return null;

    return (
      <div className="mx-auto mb-6 w-full max-w-3xl px-3 sm:px-6">
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-[#1F4E79]">Form Progress</span>
            <span className="font-semibold text-[#1F4E79]">{percentage}%</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#1F69A6] to-[#2563EB] transition-all duration-500"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  renderApplicantName = (id, applicant) => {
    if (!this.isPreviewMode() && this.props.applicants && this.props.applicants[id] && this.props.applicants[id].name) {
      return this.props.applicants[id].name;
    }
    
    const firstName = (applicant.given_name || "").trim();
    const lastName = (applicant.family_name || "").trim();
    const label = `${firstName} ${lastName}`.trim();
    if (label.length > 0) {
      return label;
    }
    return id.replace("applicant", "Applicant ").replace(/applicant-/i, "Applicant ");
  };

  renderApplicantSelectStep = () => {
    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#D6D6D6] bg-[#FFFFFF] p-4 shadow-sm sm:p-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-[#1F4E79]">General details - Step 1 of 1</p>
          <h1 className="mt-2 text-3xl font-semibold text-[#163B5C]">Select applicant</h1>
          <p className="mt-2 text-base text-[#4A4A4A]">
            Choose the applicant profile to complete now. You can switch applicants at any time.
          </p>
        </div>

        <div className="space-y-3">
          {this.state.generalDetails.applicant_order.map((id) => {
            const applicant = this.state.generalDetails.applicants[id];
            const selected = this.state.selectedApplicantId === id;
            return (
              <button
                type="button"
                key={id}
                onClick={() => this.setSelectedApplicant(id)}
                className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                  selected
                    ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                    : "border-[#CFCFCF] bg-[#FFFFFF] text-[#4A4A4A]"
                }`}
              >
                <span className="text-base font-medium">{this.renderApplicantName(id, applicant)}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => this.setCurrentStep("General_Details")}
            className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-sm font-semibold text-[#FFFFFF]"
          >
            Continue to personal details
          </button>
        </div>
      </div>
    );
  };

  renderChoiceButtons = (field, options) => {
    const selectedApplicant = this.state.generalDetails.applicants[this.state.selectedApplicantId] || {};
    const selectedValue = selectedApplicant[field] || "";
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {options.map((option) => {
          const isSelected = selectedValue === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => this.updateApplicantField(field, option)}
              className={`rounded-xl border px-3 py-3 text-sm font-medium ${
                isSelected
                  ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                  : "border-[#BDBDBD] bg-[#FFFFFF] text-[#4A4A4A]"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  };

  renderTextInput = (label, field, required = false, type = "text") => {
    const selectedApplicant = this.state.generalDetails.applicants[this.state.selectedApplicantId] || {};
    return (
      <div className="space-y-2">
        <label className="block text-base font-medium text-[#4A4A4A]">
          {label}
          {required ? <span className="text-[#B42318]"> *</span> : null}
        </label>
        <input
          type={type}
          required={required}
          value={selectedApplicant[field] || ""}
          onChange={(e) => this.updateApplicantField(field, e.target.value)}
          className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
        />
      </div>
    );
  };

  renderDateRow = (label, field, required = false) => {
    const selectedApplicant = this.state.generalDetails.applicants[this.state.selectedApplicantId] || {};
    const value = selectedApplicant[field] || {};
    return (
      <div className="space-y-2">
        <label className="block text-base font-medium text-[#4A4A4A]">
          {label}
          {required ? <span className="text-[#B42318]"> *</span> : null}
        </label>
        <div className="grid grid-cols-3 gap-2">
          <input
            type="text"
            inputMode="numeric"
            placeholder="DD"
            required={required}
            value={value.day || ""}
            onChange={(e) => this.updateApplicantDatePart(field, "day", e.target.value)}
            className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="MM"
            required={required}
            value={value.month || ""}
            onChange={(e) => this.updateApplicantDatePart(field, "month", e.target.value)}
            className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="YYYY"
            required={required}
            value={value.year || ""}
            onChange={(e) => this.updateApplicantDatePart(field, "year", e.target.value)}
            className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
          />
        </div>
      </div>
    );
  };

  renderDependantChildren = () => {
    const selectedApplicant = this.state.generalDetails.applicants[this.state.selectedApplicantId] || {};
    const count = Number(selectedApplicant.dependant_children || 0);
    const rows = [];
    for (let index = 0; index < count; index += 1) {
      const label = `Age of dependent child ${index + 1}`;
      const key = `dependant_child_age_${index + 1}`;
      rows.push(
        <div key={key} className="space-y-2">
          <label className="block text-base font-medium text-[#4A4A4A]">{label}</label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedApplicant[key] || ""}
            onChange={(e) => this.updateDependantChildAge(index, e.target.value)}
            className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
          />
        </div>
      );
    }
    return rows;
  };

  renderGeneralDetailsStep = () => {
    const selectedApplicant = this.state.generalDetails.applicants[this.state.selectedApplicantId] || {};
    const selectedApplicantName = this.renderApplicantName(
      this.state.selectedApplicantId,
      selectedApplicant
    );
    const titleOptions = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Mx", "Other"];
    const genderOptions = ["Male", "Female", "Non-Binary"];
    const maritalOptions = ["Single", "Married", "Defacto", "Divorced", "Separated", "Widowed"];
    const residentOptions = ["Citizen", "Permanent Resident", "Visa", "Other"];

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#D6D6D6] bg-[#FFFFFF] p-4 shadow-sm sm:p-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-[#1F4E79]">
            About you - General details ({selectedApplicantName})
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-[#163B5C]">Personal details</h1>
          <p className="mt-2 text-base text-[#4A4A4A]">
            We use these details to verify identity. To switch applicants, return to the selection screen.
          </p>
        </div>

        <form onSubmit={this.completeGeneralDetailsCategory} className="space-y-6">
          <div className="flex justify-between border-b border-[#E0E0E0] pb-3">
            <button
              type="button"
              onClick={() => this.setCurrentStep("Applicant_Select")}
              className="text-sm font-medium text-[#1F4E79] underline"
            >
              ← Back to applicant selection
            </button>
          </div>

          {/* Personal Identity Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Personal Identity
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Title <span className="text-[#B42318]">*</span>
                </label>
                {this.renderChoiceButtons("title", titleOptions)}
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Gender <span className="text-[#B42318]">*</span>
                </label>
                {this.renderChoiceButtons("gender", genderOptions)}
              </div>

              {this.renderTextInput("First name", "given_name", true)}
              {this.renderTextInput("Middle name", "middle_name")}
              {this.renderTextInput("Last name", "family_name", true)}
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Contact Information
            </h3>
            <div className="space-y-4">
              {this.renderTextInput("Email", "email", true, "email")}
              {this.renderTextInput("Mobile phone number", "mobile", true, "tel")}
              {this.renderTextInput("Work phone number", "work_phone", false, "tel")}
              {this.renderTextInput("Home phone number", "home_phone", false, "tel")}
            </div>
          </div>

          {/* Important Dates Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Important Dates
            </h3>
            <div className="space-y-4">
              {this.renderDateRow("Date of birth", "dob", true)}
              {this.renderDateRow("Drivers licence expiry", "licence_expiry")}
            </div>
          </div>

          {/* Personal Details Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Personal Details
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Marital status <span className="text-[#B42318]">*</span>
                </label>
                {this.renderChoiceButtons("martial_status", maritalOptions)}
              </div>

              {this.renderTextInput("Maiden name", "maiden_name")}

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Resident status <span className="text-[#B42318]">*</span>
                </label>
                {this.renderChoiceButtons("resident_status", residentOptions)}
              </div>
            </div>
          </div>

          {/* Dependents Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Dependents
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Number of dependent children <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  required
                  value={selectedApplicant.dependant_children || ""}
                  onChange={(e) => this.updateApplicantField("dependant_children", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              {this.renderDependantChildren()}
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF]"
            >
              Continue to Address History
            </button>
          </div>
        </form>
      </div>
    );
  };

  renderAddressStep = () => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantAddress = this.state.addressDetails.applicants[selectedApplicantId] || {
      current: {},
      previous: [],
    };
    const selectedApplicant = this.state.generalDetails.applicants[selectedApplicantId] || {};
    const selectedApplicantName = this.renderApplicantName(selectedApplicantId, selectedApplicant);
    
    const addressTypeOptions = ["Renting", "Owned", "Living with parents", "Boarding", "Other"];
    const stateOptions = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

    const otherApplicants = this.state.addressDetails.applicant_order.filter(
      (id) => id !== selectedApplicantId
    );

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#D6D6D6] bg-[#FFFFFF] p-4 shadow-sm sm:p-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-[#1F4E79]">
            Address History - 3 years ({selectedApplicantName})
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-[#163B5C]">Current & Previous Addresses</h1>
          <p className="mt-2 text-base text-[#4A4A4A]">
            Provide your current address and previous addresses to cover at least 3 years of history.
          </p>
        </div>

        {this.state.validationError && (
          <div className="mb-4 rounded-xl border border-[#F87171] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
            {this.state.validationError}
          </div>
        )}

        <form onSubmit={this.completeAddressCategory} className="space-y-6">
          <div className="flex justify-between border-b border-[#E0E0E0] pb-3">
            <button
              type="button"
              onClick={() => this.setCurrentStep("Applicant_Select")}
              className="text-sm font-medium text-[#1F4E79] underline"
            >
              ← Back to applicant selection
            </button>
          </div>

          {otherApplicants.length > 0 && (
            <div className="rounded-xl border-2 border-[#1F69A6] bg-gradient-to-br from-[#EBF5FF] to-[#F0F9FF] p-5 shadow-md">
              <div className="mb-3 flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#1F69A6]">
                  <svg
                    className="h-5 w-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-bold text-[#1F4E79]">Quick Copy Feature</h4>
                  <p className="mt-1 text-sm text-[#4A4A4A]">
                    Save time! Copy address history from another applicant if you share the same addresses.
                  </p>
                </div>
              </div>
              <select
                className="w-full rounded-lg border-2 border-[#1F69A6] bg-white px-4 py-3 text-base font-medium text-[#1F4E79] shadow-sm outline-none transition focus:border-[#1F4E79] focus:ring-2 focus:ring-[#B8E0FF]"
                onChange={(e) => {
                  if (e.target.value) {
                    this.copyAddressFromApplicant(e.target.value);
                    e.target.value = "";
                  }
                }}
                defaultValue=""
              >
                <option value="">Select applicant to copy from...</option>
                {otherApplicants.map((id) => {
                  const otherApplicant = this.state.generalDetails.applicants[id] || {};
                  return (
                    <option key={id} value={id}>
                      Copy from: {this.renderApplicantName(id, otherApplicant)}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Current Address Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Current Address
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Street Address <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={applicantAddress.current.street || ""}
                  onChange={(e) => this.updateAddressField("street", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Suburb <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={applicantAddress.current.suburb || ""}
                  onChange={(e) => this.updateAddressField("suburb", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    State <span className="text-[#B42318]">*</span>
                  </label>
                  <select
                    required
                    value={applicantAddress.current.state || ""}
                    onChange={(e) => this.updateAddressField("state", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  >
                    <option value="">Select...</option>
                    {stateOptions.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Postcode <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={applicantAddress.current.postcode || ""}
                    onChange={(e) => this.updateAddressField("postcode", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Accommodation Type <span className="text-[#B42318]">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {addressTypeOptions.map((type) => {
                    const isSelected = applicantAddress.current.address_type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => this.updateAddressField("address_type", type)}
                        className={`rounded-xl border px-3 py-3 text-sm font-medium ${
                          isSelected
                            ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                            : "border-[#BDBDBD] bg-[#FFFFFF] text-[#4A4A4A]"
                        }`}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>

              {(applicantAddress.current.address_type === "Renting" || 
                applicantAddress.current.address_type === "Boarding") && (
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Rent/Board Amount (per week) <span className="text-[#B42318]">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-base text-[#6B7280]">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      required
                      value={applicantAddress.current.address_amount || ""}
                      onChange={(e) => this.updateAddressField("address_amount", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 pl-8 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                  </div>
                </div>
              )}

              {applicantAddress.current.address_type === "Other" && (
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Please specify <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={applicantAddress.current.other_text || ""}
                    onChange={(e) => this.updateAddressField("other_text", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Date moved in <span className="text-[#B42318]">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="DD"
                    required
                    value={applicantAddress.current.moved_in?.day || ""}
                    onChange={(e) => this.updateAddressDatePart("moved_in", "day", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM"
                    required
                    value={applicantAddress.current.moved_in?.month || ""}
                    onChange={(e) => this.updateAddressDatePart("moved_in", "month", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="YYYY"
                    required
                    value={applicantAddress.current.moved_in?.year || ""}
                    onChange={(e) => this.updateAddressDatePart("moved_in", "year", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Previous Addresses Section */}
          {applicantAddress.previous.map((prevAddr, index) => (
            <div key={index} className="space-y-4 rounded-xl border-2 border-[#1F4E79] bg-[#F9FAFB] p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
                  Previous Address {index + 1}
                </h3>
                <button
                  type="button"
                  onClick={() => this.deletePreviousAddress(index)}
                  className="rounded-lg bg-[#DC2626] px-3 py-1.5 text-sm font-medium text-white"
                >
                  Delete
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Street Address <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={prevAddr.street || ""}
                    onChange={(e) => this.updatePreviousAddressField(index, "street", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Suburb <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={prevAddr.suburb || ""}
                    onChange={(e) => this.updatePreviousAddressField(index, "suburb", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      State <span className="text-[#B42318]">*</span>
                    </label>
                    <select
                      required
                      value={prevAddr.state || ""}
                      onChange={(e) => this.updatePreviousAddressField(index, "state", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    >
                      <option value="">Select...</option>
                      {stateOptions.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Postcode <span className="text-[#B42318]">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      required
                      value={prevAddr.postcode || ""}
                      onChange={(e) => this.updatePreviousAddressField(index, "postcode", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Accommodation Type <span className="text-[#B42318]">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {addressTypeOptions.map((type) => {
                      const isSelected = prevAddr.address_type === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => this.updatePreviousAddressField(index, "address_type", type)}
                          className={`rounded-xl border px-3 py-3 text-sm font-medium ${
                            isSelected
                              ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                              : "border-[#BDBDBD] bg-[#FFFFFF] text-[#4A4A4A]"
                          }`}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(prevAddr.address_type === "Renting" || prevAddr.address_type === "Boarding") && (
                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Rent/Board Amount (per week) <span className="text-[#B42318]">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-3.5 text-base text-[#6B7280]">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        required
                        value={prevAddr.address_amount || ""}
                        onChange={(e) => this.updatePreviousAddressField(index, "address_amount", e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 pl-8 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                  </div>
                )}

                {prevAddr.address_type === "Other" && (
                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Please specify <span className="text-[#B42318]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={prevAddr.other_text || ""}
                      onChange={(e) => this.updatePreviousAddressField(index, "other_text", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Date moved in <span className="text-[#B42318]">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD"
                        required
                        value={prevAddr.moved_in?.day || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_in", "day", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        required
                        value={prevAddr.moved_in?.month || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_in", "month", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="YYYY"
                        required
                        value={prevAddr.moved_in?.year || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_in", "year", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Date moved out <span className="text-[#B42318]">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD"
                        required
                        value={prevAddr.moved_out?.day || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_out", "day", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        required
                        value={prevAddr.moved_out?.month || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_out", "month", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="YYYY"
                        required
                        value={prevAddr.moved_out?.year || ""}
                        onChange={(e) => this.updatePreviousAddressDatePart(index, "moved_out", "year", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Previous Address Button */}
          <div>
            <button
              type="button"
              onClick={this.addPreviousAddress}
              className="w-full rounded-xl border-2 border-dashed border-[#1F4E79] bg-[#F9FAFB] px-4 py-3 text-sm font-semibold text-[#1F4E79]"
            >
              + Add Previous Address
            </button>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF]"
            >
              Continue to Employment Details
            </button>
          </div>
        </form>
      </div>
    );
  };

  renderEmploymentStep = () => {
    const selectedApplicantId = this.state.selectedApplicantId;
    const applicantEmployment = this.state.employmentDetails.applicants[selectedApplicantId] || {
      current: {},
      previous: [],
    };
    const selectedApplicant = this.state.generalDetails.applicants[selectedApplicantId] || {};
    const selectedApplicantName = this.renderApplicantName(selectedApplicantId, selectedApplicant);
    
    const employmentStatusOptions = ["Full Time", "Part Time", "Casual", "Contract", "Self Employed"];

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#D6D6D6] bg-[#FFFFFF] p-4 shadow-sm sm:p-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-[#1F4E79]">
            Employment History - 3 years ({selectedApplicantName})
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-[#163B5C]">Current & Previous Employment</h1>
          <p className="mt-2 text-base text-[#4A4A4A]">
            Provide your current employment and previous roles to cover at least 3 years of history.
          </p>
        </div>

        {this.state.validationError && (
          <div className="mb-4 rounded-xl border border-[#F87171] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
            {this.state.validationError}
          </div>
        )}

        <form onSubmit={this.completeEmploymentCategory} className="space-y-6">
          <div className="flex justify-between border-b border-[#E0E0E0] pb-3">
            <button
              type="button"
              onClick={() => this.setCurrentStep("Applicant_Select")}
              className="text-sm font-medium text-[#1F4E79] underline"
            >
              ← Back to applicant selection
            </button>
          </div>

          {/* Current Employment Section */}
          <div className="space-y-4 rounded-xl bg-[#F9FAFB] p-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
              Current Employment
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Position / Job Title <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={applicantEmployment.current.position || ""}
                  onChange={(e) => this.updateEmploymentField("position", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Employer Name <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={applicantEmployment.current.employer_name || ""}
                  onChange={(e) => this.updateEmploymentField("employer_name", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Employer Phone <span className="text-[#B42318]">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={applicantEmployment.current.employer_phone || ""}
                  onChange={(e) => this.updateEmploymentField("employer_phone", e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Gross Annual Income <span className="text-[#B42318]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-3.5 text-base text-[#6B7280]">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    value={applicantEmployment.current.gross_income || ""}
                    onChange={(e) => this.updateEmploymentField("gross_income", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 pl-8 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Employment Status <span className="text-[#B42318]">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {employmentStatusOptions.map((status) => {
                    const isSelected = applicantEmployment.current.employment_status === status;
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => this.updateEmploymentField("employment_status", status)}
                        className={`rounded-xl border px-3 py-3 text-sm font-medium ${
                          isSelected
                            ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                            : "border-[#BDBDBD] bg-[#FFFFFF] text-[#4A4A4A]"
                        }`}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-base font-medium text-[#4A4A4A]">
                  Start Date <span className="text-[#B42318]">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="DD"
                    required
                    value={applicantEmployment.current.start_date?.day || ""}
                    onChange={(e) => this.updateEmploymentDatePart("start_date", "day", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM"
                    required
                    value={applicantEmployment.current.start_date?.month || ""}
                    onChange={(e) => this.updateEmploymentDatePart("start_date", "month", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="YYYY"
                    required
                    value={applicantEmployment.current.start_date?.year || ""}
                    onChange={(e) => this.updateEmploymentDatePart("start_date", "year", e.target.value)}
                    className="rounded-xl border border-[#BDBDBD] px-3 py-3 text-center text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Previous Employments Section */}
          {applicantEmployment.previous.map((prevEmp, index) => (
            <div key={index} className="space-y-4 rounded-xl border-2 border-[#1F4E79] bg-[#F9FAFB] p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6B7280]">
                  Previous Employment {index + 1}
                </h3>
                <button
                  type="button"
                  onClick={() => this.deletePreviousEmployment(index)}
                  className="rounded-lg bg-[#DC2626] px-3 py-1.5 text-sm font-medium text-white"
                >
                  Delete
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Position / Job Title <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={prevEmp.position || ""}
                    onChange={(e) => this.updatePreviousEmploymentField(index, "position", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Employer Name <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={prevEmp.employer_name || ""}
                    onChange={(e) => this.updatePreviousEmploymentField(index, "employer_name", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Employer Phone <span className="text-[#B42318]">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={prevEmp.employer_phone || ""}
                    onChange={(e) => this.updatePreviousEmploymentField(index, "employer_phone", e.target.value)}
                    className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Gross Annual Income <span className="text-[#B42318]">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-base text-[#6B7280]">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      required
                      value={prevEmp.gross_income || ""}
                      onChange={(e) => this.updatePreviousEmploymentField(index, "gross_income", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-3 pl-8 text-base text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-base font-medium text-[#4A4A4A]">
                    Employment Status <span className="text-[#B42318]">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {employmentStatusOptions.map((status) => {
                      const isSelected = prevEmp.employment_status === status;
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => this.updatePreviousEmploymentField(index, "employment_status", status)}
                          className={`rounded-xl border px-3 py-3 text-sm font-medium ${
                            isSelected
                              ? "border-[#1F4E79] bg-[#E8F0F8] text-[#163B5C]"
                              : "border-[#BDBDBD] bg-[#FFFFFF] text-[#4A4A4A]"
                          }`}
                        >
                          {status}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      Start Date <span className="text-[#B42318]">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD"
                        required
                        value={prevEmp.start_date?.day || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "start_date", "day", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        required
                        value={prevEmp.start_date?.month || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "start_date", "month", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="YYYY"
                        required
                        value={prevEmp.start_date?.year || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "start_date", "year", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-base font-medium text-[#4A4A4A]">
                      End Date <span className="text-[#B42318]">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="DD"
                        required
                        value={prevEmp.end_date?.day || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "end_date", "day", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        required
                        value={prevEmp.end_date?.month || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "end_date", "month", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="YYYY"
                        required
                        value={prevEmp.end_date?.year || ""}
                        onChange={(e) => this.updatePreviousEmploymentDatePart(index, "end_date", "year", e.target.value)}
                        className="rounded-xl border border-[#BDBDBD] px-2 py-2 text-center text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Previous Employment Button */}
          <div>
            <button
              type="button"
              onClick={this.addPreviousEmployment}
              className="w-full rounded-xl border-2 border-dashed border-[#1F4E79] bg-[#F9FAFB] px-4 py-3 text-sm font-semibold text-[#1F4E79]"
            >
              + Add Previous Employment
            </button>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF]"
            >
              Save Employment Details
            </button>
            {this.state.categorySaved ? (
              <p className="mt-3 text-sm font-medium text-[#027A48]">
                Employment Details saved. Ready for your review before moving to the next category.
              </p>
            ) : null}
          </div>
        </form>
      </div>
    );
  };

  // ============ RENDER: INCOME STEP ============

  renderIncomeStep = () => {
    const expenses = this.state.incomeDetails.monthly_living_expenses_post_loan;
    const total = calculateTotal(expenses);

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-1 text-xl font-bold text-[#1F4E79]">Monthly Living Expenses</h2>
        <p className="mb-6 text-sm text-[#6B7280]">
          Following settlement of your loan
        </p>

        <form onSubmit={this.completeIncomeCategory}>
          <div className="space-y-4">
            {[
              { label: "Groceries", field: "groceries", required: true },
              { label: "Clothing and Personal Care", field: "clothing", required: true },
              { label: "Transport", field: "transport", required: true },
              { label: "Communication", field: "communication", required: true },
              { label: "Recreation and Entertainment", field: "recreation", required: true },
              { label: "Child Care", field: "child_care", required: false },
              { label: "Pet Care", field: "pet_care", required: false },
              { label: "Medical and Health", field: "health", required: true },
              { label: "Health Insurance", field: "health_insurance", required: true },
              { label: "General Basic Insurance", field: "general_insurance", required: false },
              { label: "Sickness, Accident, Life Insurance", field: "sickness", required: false },
              { label: "Public School Education", field: "public_school", required: false },
              { label: "Private School Education and Tutoring", field: "private_school", required: false },
              { label: "Higher Education and Professional Fees", field: "higher_edu", required: false },
            ].map((item) => (
              <div key={item.field} className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <label className="mb-1 text-sm font-medium text-[#374151] sm:mb-0 sm:w-1/2">
                  {item.label}
                  {item.required ? <span className="ml-1 text-[#EF4444]">*</span> : null}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="$0"
                  required={item.required}
                  value={expenses[item.field] || ""}
                  onChange={(e) => this.updateIncomeField(item.field, e.target.value)}
                  className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79] sm:w-1/2"
                />
              </div>
            ))}

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
              <label className="mb-1 text-sm font-medium text-[#374151] sm:mb-0 sm:w-1/2">
                Other (please specify)
              </label>
              <textarea
                value={expenses.other || ""}
                onChange={(e) => this.updateIncomeField("other", e.target.value)}
                placeholder="Describe any other expenses..."
                rows="3"
                className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79] sm:w-1/2"
              />
            </div>

            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="flex items-center justify-between rounded-xl bg-[#F0F9FF] px-4 py-3">
                <span className="text-base font-bold text-[#1F4E79]">Estimated Total</span>
                <span className="text-xl font-bold text-[#1F4E79]">${total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => this.setState({ currentStep: "Employment_Details" })}
              className="w-full rounded-xl border border-[#BDBDBD] bg-white px-4 py-3 text-base font-semibold text-[#374151] sm:w-auto"
            >
              Previous
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF] sm:w-auto"
            >
              Continue to Liabilities
            </button>
          </div>
        </form>
      </div>
    );
  };

  // ============ RENDER: LIABILITIES STEP ============

  renderLiabilitiesStep = () => {
    const { loans, credit_cards } = this.state.liabilitiesDetails;

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-1 text-xl font-bold text-[#1F4E79]">Liabilities & Debts</h2>
        <p className="mb-6 text-sm text-[#6B7280]">
          Please provide details of all loans and credit cards
        </p>

        <form onSubmit={this.completeLiabilitiesCategory}>
          {/* Loans Section */}
          <div className="mb-8">
            <h3 className="mb-4 text-lg font-semibold text-[#1F4E79]">Loans</h3>
            {Object.keys(loans).length === 0 ? (
              <p className="mb-4 text-sm text-[#6B7280]">No loans added yet.</p>
            ) : null}

            {Object.keys(loans).map((loanId) => {
              const loan = loans[loanId];
              return (
                <div key={loanId} className="mb-6 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#1F4E79]">Loan {loanId}</h4>
                    <button
                      type="button"
                      onClick={() => this.deleteLoan(loanId)}
                      className="text-xs font-medium text-[#EF4444]"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Bank Name"
                      required
                      value={loan.bank_name || ""}
                      onChange={(e) => this.updateLoanField(loanId, "bank_name", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Amount Owing"
                        required
                        value={loan.owing || ""}
                        onChange={(e) => this.updateLoanField(loanId, "owing", e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Repayment"
                        required
                        value={loan.repayment || ""}
                        onChange={(e) => this.updateLoanField(loanId, "repayment", e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Limit"
                      value={loan.limit || ""}
                      onChange={(e) => this.updateLoanField(loanId, "limit", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                    <input
                      type="text"
                      placeholder="Held By (e.g., Jointly, Applicant 1)"
                      value={loan.held_by || ""}
                      onChange={(e) => this.updateLoanField(loanId, "held_by", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                    <select
                      value={loan.payout || ""}
                      onChange={(e) => this.updateLoanField(loanId, "payout", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    >
                      <option value="">Payout Required?</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={this.addLoan}
              className="w-full rounded-xl border-2 border-dashed border-[#1F4E79] bg-[#F9FAFB] px-4 py-3 text-sm font-semibold text-[#1F4E79]"
            >
              + Add Loan
            </button>
          </div>

          {/* Credit Cards Section */}
          <div className="mb-6">
            <h3 className="mb-4 text-lg font-semibold text-[#1F4E79]">Credit Cards</h3>
            {Object.keys(credit_cards).length === 0 ? (
              <p className="mb-4 text-sm text-[#6B7280]">No credit cards added yet.</p>
            ) : null}

            {Object.keys(credit_cards).map((cardId) => {
              const card = credit_cards[cardId];
              return (
                <div key={cardId} className="mb-6 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#1F4E79]">Credit Card {cardId}</h4>
                    <button
                      type="button"
                      onClick={() => this.deleteCreditCard(cardId)}
                      className="text-xs font-medium text-[#EF4444]"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Bank Name"
                      required
                      value={card.bank_name || ""}
                      onChange={(e) => this.updateCreditCardField(cardId, "bank_name", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Amount Owing"
                        value={card.owing || ""}
                        onChange={(e) => this.updateCreditCardField(cardId, "owing", e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Credit Limit"
                        required
                        value={card.limit || ""}
                        onChange={(e) => this.updateCreditCardField(cardId, "limit", e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Held By (e.g., Jointly, Applicant 1)"
                      value={card.held_by || ""}
                      onChange={(e) => this.updateCreditCardField(cardId, "held_by", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    />
                    <select
                      value={card.payout || ""}
                      onChange={(e) => this.updateCreditCardField(cardId, "payout", e.target.value)}
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    >
                      <option value="">Payout Required?</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={this.addCreditCard}
              className="w-full rounded-xl border-2 border-dashed border-[#1F4E79] bg-[#F9FAFB] px-4 py-3 text-sm font-semibold text-[#1F4E79]"
            >
              + Add Credit Card
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => this.setState({ currentStep: "Income_Details" })}
              className="w-full rounded-xl border border-[#BDBDBD] bg-white px-4 py-3 text-base font-semibold text-[#374151] sm:w-auto"
            >
              Previous
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF] sm:w-auto"
            >
              Continue to Assets
            </button>
          </div>
        </form>
      </div>
    );
  };

  // ============ RENDER: ASSETS STEP ============

  renderAssetsStep = () => {
    const { properties, home_contents_value, savings, shares, supers, vehicles, assets } = this.state.assetDetails;

    const renderAssetCategory = (title, type, items, fields) => (
      <div className="mb-8">
        <h3 className="mb-4 text-lg font-semibold text-[#1F4E79]">{title}</h3>
        {Object.keys(items).length === 0 ? (
          <p className="mb-4 text-sm text-[#6B7280]">No {title.toLowerCase()} added yet.</p>
        ) : null}

        {Object.keys(items).map((id) => {
          const item = items[id];
          return (
            <div key={id} className="mb-6 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[#1F4E79]">
                  {title.slice(0, -1)} {id}
                </h4>
                <button
                  type="button"
                  onClick={() => this.deleteAsset(type, id)}
                  className="text-xs font-medium text-[#EF4444]"
                >
                  Remove
                </button>
              </div>

              <div className="space-y-3">
                {fields.map((field) => (
                  <div key={field.key}>
                    {field.type === "select" ? (
                      <select
                        value={item[field.key] || ""}
                        onChange={(e) => this.updateAssetField(type, id, field.key, e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      >
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        inputMode={field.inputMode || "text"}
                        placeholder={field.placeholder}
                        required={field.required}
                        value={item[field.key] || ""}
                        onChange={(e) => this.updateAssetField(type, id, field.key, e.target.value)}
                        className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => this.addAsset(type)}
          className="w-full rounded-xl border-2 border-dashed border-[#1F4E79] bg-[#F9FAFB] px-4 py-3 text-sm font-semibold text-[#1F4E79]"
        >
          + Add {title.slice(0, -1)}
        </button>
      </div>
    );

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-1 text-xl font-bold text-[#1F4E79]">Assets</h2>
        <p className="mb-6 text-sm text-[#6B7280]">
          Please provide details of all your assets
        </p>

        <form onSubmit={this.completeAssetsCategory}>
          {renderAssetCategory("Properties", "properties", properties, [
            { key: "address", placeholder: "Property Address", required: true },
            { key: "value", placeholder: "Estimated Value", inputMode: "numeric", required: true },
            { key: "owing", placeholder: "Amount Owing", inputMode: "numeric" },
            { key: "rental_income", placeholder: "Rental Income", inputMode: "numeric" },
            { key: "held_by", placeholder: "Held By" },
            {
              key: "payout",
              type: "select",
              options: ["Payout Required?", "Yes", "No"],
            },
          ])}

          <div className="mb-8">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Home Contents Value
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="$0"
              value={home_contents_value}
              onChange={(e) => this.updateHomeContentsValue(e.target.value)}
              className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
            />
          </div>

          {renderAssetCategory("Savings Accounts", "savings", savings, [
            { key: "name", placeholder: "Account Name", required: true },
            { key: "value", placeholder: "Current Balance", inputMode: "numeric", required: true },
            { key: "held_by", placeholder: "Held By" },
          ])}

          {renderAssetCategory("Shares", "shares", shares, [
            { key: "name", placeholder: "Share Name", required: true },
            { key: "value", placeholder: "Current Value", inputMode: "numeric", required: true },
            { key: "held_by", placeholder: "Held By" },
          ])}

          {renderAssetCategory("Superannuation", "supers", supers, [
            { key: "name", placeholder: "Super Fund Name", required: true },
            { key: "value", placeholder: "Current Balance", inputMode: "numeric", required: true },
            { key: "held_by", placeholder: "Held By" },
          ])}

          {renderAssetCategory("Vehicles", "vehicles", vehicles, [
            { key: "make", placeholder: "Make", required: true },
            { key: "model", placeholder: "Model", required: true },
            { key: "year", placeholder: "Year", inputMode: "numeric" },
            { key: "value", placeholder: "Estimated Value", inputMode: "numeric", required: true },
            { key: "owing", placeholder: "Amount Owing", inputMode: "numeric" },
            { key: "held_by", placeholder: "Held By" },
            {
              key: "payout",
              type: "select",
              options: ["Payout Required?", "Yes", "No"],
            },
          ])}

          {renderAssetCategory("Other Assets", "assets", assets, [
            { key: "name", placeholder: "Asset Name", required: true },
            { key: "value", placeholder: "Estimated Value", inputMode: "numeric", required: true },
            { key: "held_by", placeholder: "Held By" },
          ])}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => this.setState({ currentStep: "Liabilities_Debts" })}
              className="w-full rounded-xl border border-[#BDBDBD] bg-white px-4 py-3 text-base font-semibold text-[#374151] sm:w-auto"
            >
              Previous
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF] sm:w-auto"
            >
              Continue to Loan Requirements
            </button>
          </div>
        </form>
      </div>
    );
  };

  // ============ RENDER: LOAN REQUIREMENTS STEP ============

  renderLoanRequirementsStep = () => {
    const {
      why,
      what_can_assist,
      property,
      payment_plan,
      loan_term,
      loan_term_other,
      changes_in_financial_circum,
      changes_in_financial_circum_yes,
      loan_repayment_type,
      interest_only_term,
      rate_type,
      important_features,
    } = this.state.loanRequirements;

    const renderCheckboxSection = (title, category, items) => (
      <div className="mb-6">
        <h3 className="mb-3 text-base font-semibold text-[#1F4E79]">{title}</h3>
        <div className="space-y-2">
          {items.map((item) => (
            <label key={item.key} className="flex items-center">
              <input
                type="checkbox"
                checked={category[item.key] || false}
                onChange={() => this.toggleLoanRequirementCheckbox(item.categoryName, item.key)}
                className="mr-3 h-4 w-4 rounded border-[#BDBDBD] text-[#1F69A6] focus:ring-[#1F69A6]"
              />
              <span className="text-sm text-[#374151]">{item.label}</span>
            </label>
          ))}
        </div>
      </div>
    );

    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-1 text-xl font-bold text-[#1F4E79]">Loan Requirements</h2>
        <p className="mb-6 text-sm text-[#6B7280]">
          Help us understand your loan requirements and preferences
        </p>

        <form onSubmit={this.completeLoanRequirementsCategory}>
          {renderCheckboxSection(
            "Why are you using our service?",
            why,
            [
              { key: "wide_choice", label: "Wide choice of lenders", categoryName: "why" },
              { key: "expert_advice", label: "Expert advice", categoryName: "why" },
              { key: "convienence", label: "Convenience", categoryName: "why" },
              { key: "legwork", label: "Let us do the legwork", categoryName: "why" },
              { key: "existing_customer", label: "Existing customer", categoryName: "why" },
            ]
          )}

          {renderCheckboxSection(
            "What can we assist you with?",
            what_can_assist,
            [
              { key: "purchase", label: "Purchase", categoryName: "what_can_assist" },
              { key: "refinance", label: "Refinance", categoryName: "what_can_assist" },
              { key: "build_new_home", label: "Build new home", categoryName: "what_can_assist" },
              { key: "existing_loan", label: "Existing loan", categoryName: "what_can_assist" },
            ]
          )}

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Property Details
            </label>
            <textarea
              value={property}
              onChange={(e) => this.updateLoanRequirementField("property", e.target.value)}
              placeholder="Please describe the property..."
              rows="3"
              className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
            />
          </div>

          {renderCheckboxSection(
            "Payment Plan",
            payment_plan,
            [
              { key: "repayment_prior_to_retirement", label: "Repayment prior to retirement", categoryName: "payment_plan" },
              { key: "downsizing", label: "Downsizing", categoryName: "payment_plan" },
              { key: "sale_of_assets", label: "Sale of assets", categoryName: "payment_plan" },
              { key: "super_income", label: "Super income", categoryName: "payment_plan" },
              { key: "super_lump_sum", label: "Super lump sum", categoryName: "payment_plan" },
              { key: "savings", label: "Savings", categoryName: "payment_plan" },
              { key: "other_investments", label: "Other investments", categoryName: "payment_plan" },
              { key: "co_app_income", label: "Co-applicant income", categoryName: "payment_plan" },
              { key: "other_check", label: "Other", categoryName: "payment_plan" },
            ]
          )}

          {payment_plan.other_check ? (
            <div className="mb-6">
              <input
                type="text"
                value={payment_plan.other || ""}
                onChange={(e) =>
                  this.setState({
                    loanRequirements: {
                      ...this.state.loanRequirements,
                      payment_plan: {
                        ...this.state.loanRequirements.payment_plan,
                        other: e.target.value,
                      },
                    },
                  })
                }
                placeholder="Please specify other payment plan..."
                className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
              />
            </div>
          ) : null}

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Loan Term (years)
            </label>
            <select
              value={loan_term}
              onChange={(e) => this.updateLoanRequirementField("loan_term", e.target.value)}
              className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
            >
              <option value="">Select loan term...</option>
              <option value="5">5 years</option>
              <option value="10">10 years</option>
              <option value="15">15 years</option>
              <option value="20">20 years</option>
              <option value="25">25 years</option>
              <option value="30">30 years</option>
              <option value="other">Other</option>
            </select>
          </div>

          {loan_term === "other" ? (
            <div className="mb-6">
              <input
                type="text"
                value={loan_term_other}
                onChange={(e) => this.updateLoanRequirementField("loan_term_other", e.target.value)}
                placeholder="Please specify loan term..."
                className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
              />
            </div>
          ) : null}

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Expected changes in financial circumstances
            </label>
            <div className="mb-3 space-y-2">
              {["Yes", "No"].map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="financial_changes"
                    value={option}
                    checked={changes_in_financial_circum === option}
                    onChange={(e) =>
                      this.updateLoanRequirementField("changes_in_financial_circum", e.target.value)
                    }
                    className="mr-3 h-4 w-4 border-[#BDBDBD] text-[#1F69A6] focus:ring-[#1F69A6]"
                  />
                  <span className="text-sm text-[#374151]">{option}</span>
                </label>
              ))}
            </div>

            {changes_in_financial_circum === "Yes" ? (
              <textarea
                value={changes_in_financial_circum_yes}
                onChange={(e) =>
                  this.updateLoanRequirementField("changes_in_financial_circum_yes", e.target.value)
                }
                placeholder="Please describe the expected changes..."
                rows="3"
                className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
              />
            ) : null}
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Loan Repayment Type
            </label>
            <select
              value={loan_repayment_type}
              onChange={(e) => this.updateLoanRequirementField("loan_repayment_type", e.target.value)}
              className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
            >
              <option value="">Select repayment type...</option>
              <option value="Principal and Interest">Principal and Interest</option>
              <option value="Interest Only">Interest Only</option>
            </select>
          </div>

          {loan_repayment_type === "Interest Only" ? (
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-[#374151]">
                Interest Only Term (years)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={interest_only_term}
                onChange={(e) => this.updateLoanRequirementField("interest_only_term", e.target.value)}
                placeholder="Number of years..."
                className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
              />
            </div>
          ) : null}

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              Rate Type
            </label>
            <select
              value={rate_type}
              onChange={(e) => this.updateLoanRequirementField("rate_type", e.target.value)}
              className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
            >
              <option value="">Select rate type...</option>
              <option value="Variable">Variable</option>
              <option value="Fixed">Fixed</option>
              <option value="Split">Split</option>
            </select>
          </div>

          <div className="mb-6">
            <h3 className="mb-3 text-base font-semibold text-[#1F4E79]">Important Features</h3>
            <div className="space-y-4">
              {[
                { key: "basic_home_loan", label: "Basic Home Loan" },
                { key: "offset_account", label: "Offset Account" },
                { key: "redraw_facility", label: "Redraw Facility" },
                { key: "professional_packages", label: "Professional Packages" },
                { key: "extra_repayments_allowed", label: "Extra Repayments Allowed" },
              ].map((feature) => (
                <div key={feature.key} className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-[#374151]">{feature.label}</span>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={important_features[feature.key]?.required || false}
                        onChange={(e) =>
                          this.updateImportantFeature(feature.key, "required", e.target.checked)
                        }
                        className="mr-2 h-4 w-4 rounded border-[#BDBDBD] text-[#1F69A6] focus:ring-[#1F69A6]"
                      />
                      <span className="text-xs text-[#6B7280]">Required</span>
                    </label>
                  </div>

                  {important_features[feature.key]?.required ? (
                    <select
                      value={important_features[feature.key]?.importance || ""}
                      onChange={(e) =>
                        this.updateImportantFeature(feature.key, "importance", e.target.value)
                      }
                      className="w-full rounded-xl border border-[#BDBDBD] px-4 py-2 text-sm text-[#1E1E1E] outline-none focus:border-[#1F4E79]"
                    >
                      <option value="">Select importance...</option>
                      <option value="Essential">Essential</option>
                      <option value="Important">Important</option>
                      <option value="Nice to have">Nice to have</option>
                    </select>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => this.setState({ currentStep: "Assets" })}
              className="w-full rounded-xl border border-[#BDBDBD] bg-white px-4 py-3 text-base font-semibold text-[#374151] sm:w-auto"
            >
              Previous
            </button>
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1F69A6] px-4 py-3 text-base font-semibold text-[#FFFFFF] sm:w-auto"
            >
              Submit Application
            </button>
          </div>
        </form>
      </div>
    );
  };

  // ============ RENDER: COMPLETE STEP ============

  renderCompleteStep = () => {
    return (
      <div className="mx-auto w-full max-w-3xl rounded-2xl bg-white p-6 text-center shadow-sm sm:p-8">
        <div className="mb-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#D1FAE5]">
            <svg
              className="h-8 w-8 text-[#065F46]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-[#1F4E79]">Application Complete</h2>
          <p className="text-sm text-[#6B7280]">
            Thank you for completing your fact find form. Our team will review your information and
            be in touch shortly.
          </p>
        </div>

        <button
          onClick={() => this.setState({ currentStep: "Applicant_Select" })}
          className="rounded-xl border border-[#BDBDBD] bg-white px-6 py-3 text-base font-semibold text-[#374151]"
        >
          Return to Start
        </button>
      </div>
    );
  };

  renderContent = () => {
    if (this.isPreviewMode()) {
      if (this.state.currentStep === "Applicant_Select") {
        return this.renderApplicantSelectStep();
      }
      if (this.state.currentStep === "General_Details") {
        return this.renderGeneralDetailsStep();
      }
      if (this.state.currentStep === "Address_3y") {
        return this.renderAddressStep();
      }
      if (this.state.currentStep === "Employment_Details") {
        return this.renderEmploymentStep();
      }
      if (this.state.currentStep === "Income_Details") {
        return this.renderIncomeStep();
      }
      if (this.state.currentStep === "Liabilities_Debts") {
        return this.renderLiabilitiesStep();
      }
      if (this.state.currentStep === "Assets") {
        return this.renderAssetsStep();
      }
      if (this.state.currentStep === "Loan_Requirements") {
        return this.renderLoanRequirementsStep();
      }
      if (this.state.currentStep === "Complete") {
        return this.renderCompleteStep();
      }
      return this.renderApplicantSelectStep();
    }

    if (this.props.id === "no-form") {
      return (
        <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#F3D0D0] bg-[#FFF6F6] p-4 text-[#912018]">
          This form is unavailable for this application.
        </div>
      );
    }

    if (this.props.id == null) {
      return (
        <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#D6D6D6] bg-[#FFFFFF] p-6 text-center">
          <p className="text-base text-[#4A4A4A]">Loading your form...</p>
        </div>
      );
    }

    if (this.state.currentStep === "Applicant_Select") {
      return this.renderApplicantSelectStep();
    }

    if (this.state.currentStep === "General_Details") {
      return this.renderGeneralDetailsStep();
    }

    if (this.state.currentStep === "Address_3y") {
      return this.renderAddressStep();
    }

    if (this.state.currentStep === "Employment_Details") {
      return this.renderEmploymentStep();
    }

    if (this.state.currentStep === "Income_Details") {
      return this.renderIncomeStep();
    }

    if (this.state.currentStep === "Liabilities_Debts") {
      return this.renderLiabilitiesStep();
    }

    if (this.state.currentStep === "Assets") {
      return this.renderAssetsStep();
    }

    if (this.state.currentStep === "Loan_Requirements") {
      return this.renderLoanRequirementsStep();
    }

    if (this.state.currentStep === "Complete") {
      return this.renderCompleteStep();
    }

    return this.renderApplicantSelectStep();
  };

  render() {
    const previewMode = this.isPreviewMode();
    return (
      <div className="min-h-screen bg-[#F4F6F8] px-3 py-5 sm:px-6 sm:py-8">
        <Helmet>
          <title>Fact Find v2 - Mortgage Choice Sydney CBD</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
          />
          <link rel="stylesheet" href={process.env.PUBLIC_URL + "/css/style.css"} />
        </Helmet>
        {previewMode ? (
          <div className="mx-auto mb-4 w-full max-w-3xl rounded-xl border border-[#B8E0FF] bg-[#EAF5FF] px-4 py-3 text-sm font-medium text-[#1F4E79]">
            Preview mode: backend disabled for this page (`/factfindform-v2/test`).
          </div>
        ) : null}
        {this.renderProgressBar()}
        {this.renderContent()}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    id: state.id,
    data: state.data,
    applicants: state.applicants,
  };
};

export default connect(mapStateToProps, { fetchForm, updateData })(FormV2);

